import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { TierBadge } from '../components/ui/TierBadge';

export default function ReferEarn() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="eyebrow mb-3 text-violet-600">AFFILIATE PROGRAM</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Earn <span className="text-gradient">cash + tokens</span> for every friend you bring
        </h1>
        <p className="mt-4 text-ink-700 text-lg max-w-2xl mx-auto">
          Three tiers. Real USDT to your wallet. Plus AIML tokens — your stake in the project
          growing. No locks. No expiry. Referrals are yours forever.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/auth/signup">
            <Button variant="gradient" size="lg">
              Get my referral link
            </Button>
          </Link>
          <Link to="/buy">
            <Button variant="outline" size="lg">
              Buy $AIML
            </Button>
          </Link>
        </div>
      </div>

      {/* The 3 tiers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {/* Bronze */}
        <Card className="!border-amber-200 !bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="text-center">
            <div className="mb-3">
              <TierBadge tier="BRONZE" size="lg" />
            </div>
            <div className="eyebrow text-amber-700">Starting line</div>
            <h3 className="font-display text-2xl font-bold mt-2">$150 personal buy</h3>
            <div className="mt-6 space-y-3">
              <div>
                <div className="text-3xl font-display font-bold text-ink">15%</div>
                <div className="text-xs text-ink-600 uppercase tracking-wider">USDT cash</div>
              </div>
              <div>
                <div className="text-3xl font-display font-bold text-ink">2%</div>
                <div className="text-xs text-ink-600 uppercase tracking-wider">AIML bonus</div>
              </div>
            </div>
            <p className="text-xs text-ink-700 mt-6">
              Buy at least $150 of AIML and your referral link goes live. Earn on every friend.
            </p>
          </div>
        </Card>

        {/* Silver */}
        <Card className="!border-slate-300 !bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="text-center">
            <div className="mb-3">
              <TierBadge tier="SILVER" size="lg" />
            </div>
            <div className="eyebrow text-slate-700">Tier upgrade</div>
            <h3 className="font-display text-2xl font-bold mt-2">$300 personal buy</h3>
            <div className="mt-6 space-y-3">
              <div>
                <div className="text-3xl font-display font-bold text-ink">20%</div>
                <div className="text-xs text-ink-600 uppercase tracking-wider">USDT cash</div>
              </div>
              <div>
                <div className="text-3xl font-display font-bold text-ink">5%</div>
                <div className="text-xs text-ink-600 uppercase tracking-wider">AIML bonus</div>
              </div>
            </div>
            <p className="text-xs text-ink-700 mt-6">
              Top up to $300 and your rate jumps to 20% cash + 5% tokens — applies to all your
              future referrals.
            </p>
          </div>
        </Card>

        {/* Founders Circle */}
        <Card className="!border-violet-300 !bg-gradient-to-br from-magenta-50 via-violet-50 to-azure-50 relative overflow-hidden">
          <div className="absolute top-0 right-0 px-3 py-1 bg-gradient-to-r from-magenta-500 via-violet-500 to-azure-500 text-white text-[10px] uppercase tracking-widest rounded-bl-2xl">
            Leadership tier
          </div>
          <div className="text-center">
            <div className="mb-3">
              <TierBadge tier="FOUNDERS_CIRCLE" size="lg" />
            </div>
            <div className="eyebrow text-violet-700">Top performers</div>
            <h3 className="font-display text-xl font-bold mt-2">$10,000 team volume</h3>
            <div className="mt-6 space-y-2">
              <div>
                <div className="text-3xl font-display font-bold text-ink">22%</div>
                <div className="text-xs text-ink-600 uppercase tracking-wider">USDT cash</div>
              </div>
              <div>
                <div className="text-3xl font-display font-bold text-ink">6%</div>
                <div className="text-xs text-ink-600 uppercase tracking-wider">AIML bonus</div>
              </div>
              <div className="pt-2 border-t border-violet-200">
                <div className="text-3xl font-display font-bold text-gradient">+3%</div>
                <div className="text-xs text-violet-700 uppercase tracking-wider font-semibold">
                  Leadership override
                </div>
                <div className="text-[10px] text-ink-600 mt-0.5">on indirect sales</div>
              </div>
            </div>
            <p className="text-xs text-ink-700 mt-4">
              Build a $10k team and you join the inner circle — top rates plus a 3% senior bonus
              on your team's next-generation buyers.
            </p>
          </div>
        </Card>
      </div>

      {/* How it works */}
      <div className="mb-16">
        <div className="text-center mb-8">
          <div className="eyebrow text-violet-600">HOW IT WORKS</div>
          <h2 className="font-display text-3xl font-bold mt-2">Four steps. That's it.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              n: '1',
              title: 'Buy $150 in AIML',
              body: 'Your one-time stake. You own those tokens — they\'re yours regardless of referrals.',
            },
            {
              n: '2',
              title: 'Get your referral link',
              body: 'Automatically generated when you become Bronze. Share via WhatsApp, social media, anywhere.',
            },
            {
              n: '3',
              title: 'Earn instantly',
              body: 'Every purchase through your link pays you USDT + AIML, sent straight to your wallet.',
            },
            {
              n: '4',
              title: 'Climb the tiers',
              body: 'Top up to Silver, build a team to reach Founders Circle. Your rate goes up. Forever.',
            },
          ].map((step) => (
            <Card key={step.n} className="text-center">
              <div className="text-3xl font-display font-bold text-gradient mb-3">{step.n}</div>
              <h3 className="font-display font-bold text-lg mb-2">{step.title}</h3>
              <p className="text-sm text-ink-700">{step.body}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* What you actually earn */}
      <Card className="mb-16 !bg-gradient-to-br from-violet-50 via-white to-magenta-50">
        <div className="text-center">
          <div className="eyebrow text-violet-600">REAL NUMBERS</div>
          <h2 className="font-display text-3xl font-bold mt-2 mb-6">
            What you earn at each tier
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-paper-300">
                <th className="px-3 py-3 text-left eyebrow">Friend buys</th>
                <th className="px-3 py-3 text-right eyebrow">Bronze earns</th>
                <th className="px-3 py-3 text-right eyebrow">Silver earns</th>
                <th className="px-3 py-3 text-right eyebrow">Founders Circle earns</th>
              </tr>
            </thead>
            <tbody>
              {[
                { amt: 100, b: 15, s: 20, fc: 22 },
                { amt: 500, b: 75, s: 100, fc: 110 },
                { amt: 1000, b: 150, s: 200, fc: 220 },
                { amt: 5000, b: 750, s: 1000, fc: 1100 },
              ].map((row) => (
                <tr key={row.amt} className="border-b border-paper-200">
                  <td className="px-3 py-3 font-mono">${row.amt}</td>
                  <td className="px-3 py-3 text-right font-mono font-semibold">
                    ${row.b} + {(row.amt * 0.02).toFixed(0)}% AIML
                  </td>
                  <td className="px-3 py-3 text-right font-mono font-semibold">
                    ${row.s} + {(row.amt * 0.05).toFixed(0)}% AIML
                  </td>
                  <td className="px-3 py-3 text-right font-mono font-semibold text-gradient">
                    ${row.fc} + {(row.amt * 0.06).toFixed(0)}% AIML
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink-600 mt-4 text-center">
          USDT amounts are exact. AIML bonus shown as percent of the purchase value — actual
          tokens depend on the current rate (~8,500 AIML per $1 USDT today).
        </p>
      </Card>

      {/* Founders Circle deep-dive */}
      <Card className="mb-16 !border-violet-300 !bg-gradient-to-br from-magenta-50 via-violet-50 to-azure-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="eyebrow text-violet-700 mb-2">🥇 FOUNDERS CIRCLE</div>
            <h2 className="font-display text-3xl font-bold mb-4">
              The leadership tier
            </h2>
            <p className="text-ink-700 mb-4">
              When your team's combined purchases hit $10,000, you join the Founders Circle.
              Top rates on your direct sales, plus a <strong>3% leadership override</strong> on
              the indirect sales your team brings in.
            </p>
            <p className="text-ink-700 mb-4">
              About <strong>20 referrals</strong> at $500 each gets you there. Reachable.
              Realistic.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-violet-600 font-bold">✓</span>
                <span>22% USDT + 6% AIML on direct referrals</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-600 font-bold">✓</span>
                <span>+3% USDT on indirect sales (one level deep)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-600 font-bold">✓</span>
                <span>Direct line to the project team</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-600 font-bold">✓</span>
                <span>First access to every launch and announcement</span>
              </li>
            </ul>
          </div>
          <div className="bg-white/60 rounded-2xl p-6">
            <div className="text-xs uppercase tracking-wider text-violet-700 mb-2">
              Worked example
            </div>
            <p className="text-sm text-ink-700 mb-3">
              You're Founders Circle. Priya is one of your direct referrals (Silver tier).
              Priya brings in Amit who buys $1,000:
            </p>
            <div className="space-y-2 text-sm font-mono">
              <div className="flex justify-between">
                <span className="text-ink-600">Priya earns (Silver):</span>
                <span className="font-bold">$200 + 5% AIML</span>
              </div>
              <div className="flex justify-between bg-violet-100 rounded-lg px-3 py-2">
                <span className="text-violet-700 font-semibold">You earn (FC override):</span>
                <span className="font-bold text-violet-700">$30 USDT</span>
              </div>
              <p className="text-xs text-ink-600 italic mt-3">
                Small per sale — but as your team scales, the overrides compound into a real
                second income stream.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* FAQ */}
      <div className="mb-12">
        <div className="text-center mb-8">
          <div className="eyebrow text-violet-600">COMMON QUESTIONS</div>
          <h2 className="font-display text-3xl font-bold mt-2">Quick answers</h2>
        </div>
        <div className="space-y-3">
          {[
            {
              q: 'How do I get paid?',
              a: 'USDT is sent straight to your crypto wallet. You can withdraw to your bank anytime via any exchange. AIML tokens go to the same wallet — hold them or trade after launch.',
            },
            {
              q: 'How long do my referrals last?',
              a: 'Forever. Once they sign up through your link, they\'re tagged as yours. Every time they buy — today, next year, anytime — you earn at your current tier rate.',
            },
            {
              q: 'What if I don\'t refer anyone?',
              a: 'No problem. Your $150 buy is your own AIML investment — you keep those tokens regardless. The referral earnings are bonus on top.',
            },
            {
              q: 'When do tier upgrades happen?',
              a: 'Instantly. The moment you cross a threshold (either by topping up your buy or by your team\'s volume hitting $10k), your new rate applies to all future sales.',
            },
            {
              q: 'Is there a minimum to buy?',
              a: 'No. You can buy any amount of AIML — even $5. The $150/$300 thresholds only matter for affiliate eligibility, not for buying.',
            },
            {
              q: 'What\'s the catch?',
              a: 'Two rules: complete KYC verification before withdrawing (standard for any compliant project), and the affiliate program requires the $150 personal buy to unlock. No locks, no quotas, no expiry.',
            },
          ].map((item) => (
            <details
              key={item.q}
              className="card-base p-5 cursor-pointer group"
            >
              <summary className="font-display font-semibold text-lg list-none flex justify-between items-center">
                {item.q}
                <span className="text-violet-500 group-open:rotate-180 transition-transform">
                  ▼
                </span>
              </summary>
              <p className="mt-3 text-sm text-ink-700">{item.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* CTA bottom */}
      <Card className="text-center !bg-gradient-to-br from-magenta-500 via-violet-500 to-azure-500 !text-white !border-0">
        <h2 className="font-display text-3xl font-bold mb-3">Ready to start earning?</h2>
        <p className="opacity-90 mb-6 max-w-xl mx-auto">
          Sign up, buy $150 of AIML, share your link. Your first commission could be in your
          wallet by tomorrow.
        </p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link to="/auth/signup">
            <Button variant="primary" size="lg" className="!bg-white !text-violet-600 hover:!bg-paper-100">
              Create my account
            </Button>
          </Link>
          <Link to="/buy">
            <Button variant="outline" size="lg" className="!border-white !text-white hover:!bg-white/10">
              Buy $AIML first
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
